import { ContentEdit } from '@fs/zion-icon'
import {
  Button,
  Cell,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Grid,
  Radio,
  RadioGroup,
  Row,
  Select,
  TextField,
} from '@fs/zion-ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { updatePerson } from '../../../../services/famousHistoricalService'
import { getBooleanDisplayValue } from '../../../../util/StringUtil'
import DataView from '../../../common/DataView'
import { DmcDatePicker } from '../../../common/DmcDatePicker/DmcDatePicker'
import HistoricalDeletePersonButton from './HistoricalDeletePersonButton'

export default function HistoricalPersonDataDialog({
  person,
  removePersonOnDelete,
  updatePersonOnEdit,
  countries,
  ...personDataOverlay
}) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const personJSON = JSON.parse(person.personJSON)
  const ogPersonData = {
    ...personJSON,
    birthDateItem: { key: 'birthDateItem', primaryText: personJSON.birthDate, formattedDate: personJSON.birthDate },
  }
  const [personData, setPersonData] = useState(ogPersonData)
  const ogCountry = person.country
  const [country, setCountry] = useState(person.country)
  const [editing, setEditing] = useState(false)
  const ogPersonFullName = `${ogPersonData.givenName} ${ogPersonData.surname}`
  const personFullName = `${personData.givenName} ${personData.surname}`
  const data = getData()

  function shouldDisableSaveButton() {
    return (
      JSON.stringify({ ...ogPersonData, birthDateItem: undefined }) ===
        JSON.stringify({ ...personData, birthDateItem: undefined }) &&
      ogPersonData.birthDate === personData.birthDateItem?.formattedDate &&
      country === person.country
    )
  }

  useEffect(() => {
    setCountry(person.country)
  }, [person])

  useEffect(() => {
    setPersonData({
      ...JSON.parse(person.personJSON),
      birthDateItem: {
        key: 'birthDateItem',
        primaryText: ogPersonData.birthDate,
        formattedDate: ogPersonData.birthDate,
      },
    })
    setEditing(false)
  }, [ogPersonData.birthDate, person])

  const onPersonChange = (fieldType, newValue) => {
    setPersonData({
      ...personData,
      [fieldType]: newValue,
    })
  }

  const doneBtnOnClick = () => {
    personDataOverlay.close()
    setPersonData({
      ...ogPersonData,
      birthDateItem: {
        key: 'birthDateItem',
        primaryText: ogPersonData.birthDate,
        formattedDate: ogPersonData.birthDate,
      },
    })
  }

  const cancelBtnOnClick = () => {
    setPersonData({
      ...ogPersonData,
      birthDateItem: {
        key: 'birthDateItem',
        primaryText: ogPersonData.birthDate,
        formattedDate: ogPersonData.birthDate,
      },
    })
    setCountry(ogCountry)
    setEditing(!editing)
  }

  const saveBtnOnClick = async () => {
    const personDataWithFormattedDates = {
      ...personData,
      birthDate: personData.birthDateItem && personData.birthDateItem.formattedDate,
    }
    delete personDataWithFormattedDates.birthDateItem

    const personDto = {
      id: person.id,
      metaDataId: person.metaDataId,
      personJSON: JSON.stringify(personDataWithFormattedDates),
      matchType: person.matchType,
      country,
    }
    try {
      await updatePerson(personDto)
      updatePersonOnEdit(person.id, personDto.personJSON, country)
      setEditing(false)
    } catch (err) {
      error(
        t('tools.errors.editingHistoricalPerson', {
          defaultValue: `Error editing historical person: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  function getData() {
    if (editing) {
      return getHistoricalDataInEditMode()
    }
    return getHistoricalDataInViewMode()
  }

  function getHistoricalDataInViewMode() {
    return [
      {
        label: <b>{t('givenName', 'Given Name')}</b>,
        detail: personData.givenName,
      },
      {
        label: <b>{t('name.parts.surname', 'Surname')}</b>,
        detail: personData.surname,
      },
      {
        label: <b>{t('personId', 'Person ID')}</b>,
        detail: personData.pid,
      },
      {
        label: <b>{t('label', 'Label')}</b>,
        detail: personData.label,
      },
      {
        label: <b>{t('tools.lists.whyFamous', 'Why Famous')}</b>,
        detail: personData.reasonWhyFamous,
      },
      {
        label: <b>{t('tools.lists.labels.birthDate', 'Birth Date')}</b>,
        detail: personData.birthDateItem.formattedDate,
      },
      {
        label: <b>{t('tools.lists.labels.needsSources', 'Needs Sources')}</b>,
        detail: getBooleanDisplayValue(personData.needsSources),
      },
      {
        label: <b>{t('accessControl.locked', 'Locked')}</b>,
        detail: getBooleanDisplayValue(personData.locked),
      },
      {
        label: <b>{t('tools.lists.labels.possibleDuplicates', 'Possible Duplicates')}</b>,
        detail: personData?.possibleDuplicates?.replace(/"/g, ''),
      },
    ]
  }

  function getHistoricalDataInEditMode() {
    return [
      {
        key: 'historicalGivenName',
        label: <b>{t('givenName', 'Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.givenName}
            onChange={(e) => onPersonChange('givenName', e.target.value)}
          />
        ),
      },
      {
        key: 'historicalSurname',
        label: <b>{t('name.parts.surname', 'Surname')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.surname}
            onChange={(e) => onPersonChange('surname', e.target.value)}
          />
        ),
      },
      {
        key: 'historicalPersonId',
        label: <b>{t('personId', 'Person ID')}</b>,
        detail: (
          <TextField type="text" value={personData.pid} onChange={(e) => onPersonChange('pid', e.target.value)} />
        ),
      },
      {
        key: 'historicalLabel',
        label: <b>{t('label', 'Label')}</b>,
        detail: (
          <Select
            placeholder={t('tools.lists.placeholders.selectLabel', 'Select Label')}
            value={personData.label}
            onChange={(e) => onPersonChange('label', e.target.value)}
            options={[
              {
                value: 'Historical',
                label: t('historical', 'Historical'),
              },
              {
                value: 'Church Leader',
                label: t('churchLeader', 'Church Leader'),
              },
            ]}
          />
        ),
      },
      {
        key: 'historicalWhyFamous',
        label: <b>{t('tools.lists.whyFamous', 'Why Famous')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData.reasonWhyFamous}
            onChange={(e) => onPersonChange('reasonWhyFamous', e.target.value)}
          />
        ),
      },
      {
        key: 'historicalBirthDate',
        label: null,
        detail: (
          <DmcDatePicker
            label={t('tools.lists.labels.birthDate', 'Birth Date')}
            selectedItem={personData.birthDateItem}
            setSelectedItem={(e) => onPersonChange('birthDateItem', e)}
            dayMonthYearDateFormat="DD MMM YYYY"
            monthYearDateFormat="yyyy"
            yearDateFormat="yyyy"
          />
        ),
      },
      {
        key: 'historicalNeedsSources',
        label: <b>{t('tools.lists.labels.needsSources', 'Needs Sources')}</b>,
        detail: (
          <RadioGroup orientation="horizontal">
            <Radio
              name="historicalNeedsSourcesRadioNo"
              checked={personData.needsSources}
              label={t('yes', 'Yes')}
              value="true"
              onChange={(e) => onPersonChange('needsSources', e.target.value === 'true')}
            />
            <Radio
              name="historicalNeedsSourcesRadioNo"
              checked={!personData.needsSources}
              label={t('no', 'No')}
              value="false"
              onChange={(e) => onPersonChange('needsSources', e.target.value === 'true')}
            />
          </RadioGroup>
        ),
      },
      {
        key: 'historicalLocked',
        label: <b>{t('accessControl.locked', 'Locked')}</b>,
        detail: (
          <RadioGroup orientation="horizontal">
            <Radio
              name="historicalLockedRadioYes"
              checked={personData.locked}
              label={t('yes', 'Yes')}
              value="true"
              onChange={(e) => onPersonChange('locked', e.target.value === 'true')}
            />
            <Radio
              name="historicalLockedRadioNo"
              checked={!personData.locked}
              label={t('no', 'No')}
              value="false"
              onChange={(e) => onPersonChange('locked', e.target.value === 'true')}
            />
          </RadioGroup>
        ),
      },
      {
        key: 'historicalPossibleDuplicates',
        label: <b>{t('tools.lists.labels.possibleDuplicates', 'Possible Duplicates')}</b>,
        detail: (
          <TextField
            type="text"
            value={personData?.possibleDuplicates?.replace(/"/g, '')}
            onChange={(e) => onPersonChange('possibleDuplicates', e.target.value)}
          />
        ),
      },
    ]
  }

  return (
    <DialogOverlay
      {...personDataOverlay}
      title={editing ? ogPersonFullName : personFullName}
      subtitle={!editing && country.replace('_', ' ')}
      size="xxl"
    >
      {personData && (
        <>
          {editing && (
            <Grid>
              <Cell columns={3}>
                <Select
                  id="country"
                  placeholder={t('tools.lists.placeholders.selectMultiAreaTeam', 'Select Multi-area Team')}
                  label={t('tools.lists.labels.multiAreaTeam', 'Multi-area Team')}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  options={countries}
                />
              </Cell>
              <p columns={9} />
            </Grid>
          )}
          <DataView data={data} />
        </>
      )}

      <DialogOverlayFooter>
        <DialogOverlayActions>
          {editing && (
            <Row>
              <Button emphasis="high" onClick={saveBtnOnClick} disabled={shouldDisableSaveButton()}>
                {t('action.save', 'Save')}
              </Button>
              <Button emphasis="medium" onClick={cancelBtnOnClick}>
                {t('action.cancel', 'Cancel')}
              </Button>
            </Row>
          )}
          {!editing && (
            <Row>
              <Button emphasis="medium" Icon={ContentEdit} onClick={() => setEditing(!editing)}>
                {t('action.edit', 'Edit')}
              </Button>
              <HistoricalDeletePersonButton
                person={person}
                personFullName={ogPersonFullName}
                removePersonOnDelete={removePersonOnDelete}
                {...personDataOverlay}
              />
              <Button emphasis="medium" onClick={doneBtnOnClick}>
                {t('action.done', 'Done')}
              </Button>
            </Row>
          )}
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
